import { FC } from 'react';

type StepProgressPipsProps = {
  totalSteps: number;
  currentStep: number;
};

const CheckIcon: FC = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Funnel-agnostic step progress display. Driven purely by a step count and
 * the active step index — no funnel-specific slugs or page names — so it
 * works for any flow. The number of pips equals `totalSteps`.
 *
 * States (per the v2 design):
 *  - complete: filled circle with a tick
 *  - current:  outlined circle with a filled dot
 *  - upcoming: grey outlined circle
 * Connectors leading into reached pips are coloured; the rest are grey.
 */
const StepProgressPips: FC<StepProgressPipsProps> = ({
  totalSteps,
  currentStep
}) => {
  if (totalSteps <= 0) return null;

  // currentStep can be >= totalSteps once the funnel is complete (e.g. the
  // trailing paywall) — clamp the aria value and label it as finished rather
  // than reporting an out-of-range "Step N+1 of N".
  const isComplete = currentStep >= totalSteps;

  return (
    <div
      className="flex w-full items-center"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-valuenow={
        currentStep >= 0 ? Math.min(currentStep + 1, totalSteps) : undefined
      }
      aria-label={
        currentStep < 0
          ? 'Progress'
          : isComplete
          ? `All ${totalSteps} steps complete`
          : `Step ${currentStep + 1} of ${totalSteps}`
      }
    >
      {Array.from({ length: totalSteps }, (_, index) => {
        const isCurrent = index === currentStep;
        const isComplete = currentStep >= 0 && index < currentStep;
        // The rail leading into this pip is coloured once its left
        // neighbour has been completed.
        const isConnectorActive = currentStep >= 0 && index <= currentStep;

        return (
          <div
            key={index}
            className="flex items-center"
            // The first pip carries no leading connector, so it should not
            // stretch; every later pip stretches its connector to fill.
            style={index === 0 ? undefined : { flex: 1 }}
          >
            {index > 0 && (
              <span
                className={`h-0.5 flex-1 transition-colors duration-300 ${
                  isConnectorActive ? 'bg-primary-500' : 'bg-neutral-300'
                }`}
              />
            )}
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                isComplete
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : isCurrent
                  ? 'border-primary-500 bg-white text-primary-500'
                  : 'border-neutral-300 bg-white text-transparent'
              }`}
            >
              {isComplete ? (
                <CheckIcon />
              ) : isCurrent ? (
                <span className="h-2 w-2 rounded-full bg-primary-500" />
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default StepProgressPips;
